import React, { useState, useCallback, useContext } from 'react';
import {
  DndContext,
  DragOverlay,
  PointerSensor,
  useSensor,
  useSensors,
  closestCorners,
} from '@dnd-kit/core';

import { Plus, Search, BarChart3, X, Loader2, ArrowDownToLine, HelpCircle, User, Users } from 'lucide-react';
import PipelineColumn from './PipelineColumn';
import LeadCard from './LeadCard';
import LeadDialog from './LeadDialog';
import ContactLogDialog from './ContactLogDialog';
import LeadProfile from './LeadProfile';
import EnrolDialog from './EnrolDialog';
import PipelineHelp from './PipelineHelp';
import usePipelineData from './usePipelineData';
import { LanguageContext } from '../../../../Context';
import { UserContext } from '../../../../Context';



/**
 * Pipeline - Marketing pipeline kanban board.
 * Displays leads across 5 sales stages with drag-and-drop,
 * modal detail view, contact logging, and duplicate phone detection.
 * Phone-centric with bilingual labels.
 *
 * Route: /sales/pipeline
 * Sidebar: Sales group, Funnel icon
 *
 * @returns {JSX.Element}
 * @author Forge, opencode glm-5.1
 * @version 2.5
 * @since 2026-04-15
 */
export default function Pipeline() {
  const { l, language } = useContext(LanguageContext);
  
  const { user } = useContext(UserContext);

  const t = useCallback((key, fallback) => {
    try { return l[key]?.[language] || fallback || key; } catch { return fallback || key; }
  }, [l, language]);

  const {
    leads,
    filteredLeads,
    stages,
    addLead,
    updateLead,
    deleteLead,
    moveLead,
    addContact,
    checkPhone,
    pullNextLead,
    convertLead,
    stats,
    searchQuery,
    setSearchQuery,
    loading,
    availableCount,
  } = usePipelineData();

  const [selectedLead, setSelectedLead] = useState(null);
  const [leadDialogOpen, setLeadDialogOpen] = useState(false);
  const [editingLead, setEditingLead] = useState(null);
  const [defaultStage, setDefaultStage] = useState('lead');
  const [contactDialogOpen, setContactDialogOpen] = useState(false);
  const [contactLead, setContactLead] = useState(null);
  const [activeLead, setActiveLead] = useState(null);
  const [showStats, setShowStats] = useState(false);
  const [pullingLead, setPullingLead] = useState(false);
  const [helpOpen, setHelpOpen] = useState(false);
  const [showAllLeads, setShowAllLeads] = useState(false);
  const [enrolDialogOpen, setEnrolDialogOpen] = useState(false);
  const [enrolLead, setEnrolLead] = useState(null);

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: { distance: 5 },
    })
  );

  const getLeadId = (lead) => lead.id || lead._id;

  const handleLeadClick = useCallback((lead) => {
    setSelectedLead(lead);
  }, []);

  const handleAddClick = useCallback((stageId) => {
    setEditingLead(null);
    setDefaultStage(stageId);
    setLeadDialogOpen(true);
  }, []);

  const handleSaveLead = useCallback((data) => {
    if (editingLead) {
      updateLead(getLeadId(editingLead), data);
      if (selectedLead && getLeadId(selectedLead) === getLeadId(editingLead)) {
        setSelectedLead(prev => ({ ...prev, ...data }));
      }
    } else {
      addLead(data);
    }
  }, [editingLead, selectedLead, addLead, updateLead]);

  const handleEditLead = useCallback((lead) => {
    setEditingLead(lead);
    setLeadDialogOpen(true);
  }, []);

  const handleUpdateLeadField = useCallback((id, updates) => {
    updateLead(id, updates);
    if (selectedLead && getLeadId(selectedLead) === id) {
      setSelectedLead(prev => ({ ...prev, ...updates }));
    }
  }, [selectedLead, updateLead]);

  const handleDeleteLead = useCallback((id) => {
    deleteLead(id);
    if (selectedLead && getLeadId(selectedLead) === id) setSelectedLead(null);
  }, [selectedLead, deleteLead]);

  const handleOpenContact = useCallback((lead) => {
    setContactLead(lead);
    setContactDialogOpen(true);
  }, []);

  const handleSaveContact = useCallback((contactData) => {
    if (!contactLead) return;
    const id = getLeadId(contactLead);
    const dataWithUser = { ...contactData, contactedBy: user?._id };
    addContact(id, dataWithUser);
    if (selectedLead && getLeadId(selectedLead) === id) {
      const newContact = { ...dataWithUser, id: Date.now(), date: new Date().toISOString() };
      setSelectedLead(prev => ({ ...prev, contacts: [...(prev.contacts || []), newContact] }));
    }
  }, [contactLead, selectedLead, addContact, user]);

  const handleEnrol = useCallback((lead) => {
    setEnrolLead(lead);
    setEnrolDialogOpen(true);
  }, []);

  const handleConverted = useCallback((leadId, conversionData) => {
    // The convertLead function will be called from EnrolDialog
    // We just need to refresh the leads or let the dialog handle it
    setEnrolDialogOpen(false);
    setEnrolLead(null);
  }, []);

  const handleViewDuplicateLead = useCallback((dupLead) => {
    setLeadDialogOpen(false);
    const fullLead = leads.find(l => getLeadId(l) === dupLead._id);
    if (fullLead) {
      setSelectedLead(fullLead);
    } else {
      setSelectedLead(dupLead);
    }
  }, [leads]);

  const handlePullNextLead = useCallback(async () => {
    setPullingLead(true);
    try {
      const lead = await pullNextLead();
      if (lead) {
        setSelectedLead(lead);
      } else {
        alert(t('pipeline.noLeadsAvailable', 'No marketing leads available'));
      }
    } finally {
      setPullingLead(false);
    }
  }, [pullNextLead, t]);

  const handleDragStart = (event) => {
    const lead = leads.find(l => getLeadId(l) === event.active.id);
    setActiveLead(lead || null);
  };

  const handleDragEnd = (event) => {
    setActiveLead(null);
    const { active, over } = event;
    if (!over) return;

    const activeId = active.id;
    let targetStage = null;

    const stageIds = stages.map(s => s.id);
    if (stageIds.includes(over.id)) {
      targetStage = over.id;
    }
    else if (over.data?.current?.lead) {
      targetStage = over.data.current.lead.stage;
    }
    else {
      const overLead = leads.find(l => getLeadId(l) === over.id);
      if (overLead) targetStage = overLead.stage;
    }

    if (!targetStage) return;

    const draggedLead = leads.find(l => getLeadId(l) === activeId);
    if (!draggedLead) return;

    if (draggedLead.stage !== targetStage) {
      moveLead(activeId, targetStage);
      if (selectedLead && getLeadId(selectedLead) === activeId) {
        setSelectedLead(prev => ({ ...prev, stage: targetStage }));
      }
      if (targetStage === 'book-test') {
        const updatedLead = leads.find(l => getLeadId(l) === activeId);
        setSelectedLead(prev => updatedLead ? { ...updatedLead, stage: 'book-test' } : { ...prev, stage: 'book-test' });
      }
    }
  };

  const baseLeads = searchQuery.trim() ? filteredLeads : leads;
  const userId = user?._id;
  const displayLeads = showAllLeads
    ? baseLeads
    : baseLeads.filter(l => l.addedBy === userId);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="flex flex-col items-center gap-3">
          <Loader2 size={24} className="text-slate-300 animate-spin" />
          <p className="text-sm text-slate-400">{t('pipeline.loading', 'Loading pipeline...')}</p>
        </div>
      </div>
    );
  }

  const translatedStages = stages.map(s => ({
    ...s,
    label: t(`pipeline.${s.id === 'add-zalo' ? 'addZalo' : s.id === 'book-test' ? 'bookTest' : s.id}`, s.label),
  }));

  return (
    <div className="flex flex-col h-full">
      {/* Top bar */}
      <div className="flex items-center justify-between mb-4 flex-shrink-0 gap-4">
        <div className="flex items-center gap-3 flex-shrink-0">
          <h1 className="text-xl font-bold text-slate-800">{t('pipeline', 'Pipeline')}</h1>
          <div className="flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-slate-100 text-xs text-slate-500">
            <BarChart3 size={12} />
            <span>{stats.total} {t('pipeline.leadsCount', 'leads')}</span>
            {stats.total > 0 && (
              <span className="text-emerald-600 font-semibold ml-1">
                {stats.conversionRate}% {t('pipeline.enrolRate', 'enrol')}
              </span>
            )}
          </div>
        </div>

        <div className="flex items-center gap-2 flex-shrink-0">
          {/* Help */}
          <button
            onClick={() => setHelpOpen(true)}
            className="w-9 h-9 rounded-[10px] flex items-center justify-center border border-slate-200 bg-white text-slate-500 hover:bg-slate-50 hover:text-slate-600 transition-colors duration-150 cursor-pointer"
            title={t('pipeline.help', 'Help')}
          >
            <HelpCircle size={16} />
          </button>

          {/* My Leads / All Leads toggle */}
          <div className="flex items-center rounded-[10px] border border-slate-200 overflow-hidden">
            <button
              onClick={() => setShowAllLeads(false)}
              className={`flex items-center gap-1.5 px-3 py-2 text-xs font-semibold transition-colors duration-150 cursor-pointer ${
                !showAllLeads
                  ? 'bg-slate-800 text-white'
                  : 'bg-white text-slate-500 hover:bg-slate-50'
              }`}
            >
              <User size={13} />
              {t('pipeline.myLeads', 'My Leads')}
            </button>
            <button
              onClick={() => setShowAllLeads(true)}
              className={`flex items-center gap-1.5 px-3 py-2 text-xs font-semibold transition-colors duration-150 cursor-pointer border-l border-slate-200 ${
                showAllLeads
                  ? 'bg-slate-800 text-white'
                  : 'bg-white text-slate-500 hover:bg-slate-50'
              }`}
            >
              <Users size={13} />
              {t('pipeline.allLeads', 'All Leads')}
            </button>
          </div>

          {/* Search */}
          <div className="relative">
            <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder={t('pipeline.searchByPhone', 'Search by phone or name...')}
              className={`pl-9 pr-8 py-2 rounded-[10px] border border-slate-200 text-sm text-slate-800 placeholder:text-slate-300 focus:outline-none focus:border-teal-800 focus:ring-1 focus:ring-teal-800/20 w-[260px] transition-all duration-150`}
            />
            {searchQuery && (
              <button
                onClick={() => setSearchQuery('')}
                className="absolute right-2.5 top-1/2 -translate-y-1/2 text-slate-300 hover:text-slate-500 transition-colors duration-150 cursor-pointer"
              >
                <X size={14} />
              </button>
            )}
          </div>

          {/* Stats toggle */}
          <button
            onClick={() => setShowStats(!showStats)}
            className={`w-9 h-9 rounded-[10px] flex items-center justify-center border transition-colors duration-150 cursor-pointer ${
              showStats
                ? 'bg-slate-800 text-white border-slate-800'
                : 'bg-white text-slate-500 border-slate-200 hover:bg-slate-50'
            }`}
          >
            <BarChart3 size={16} />
          </button>

          {/* Next Lead from marketing */}
          <button
            onClick={handlePullNextLead}
            disabled={pullingLead || availableCount === 0}
            className={`flex items-center gap-2 px-4 py-2 rounded-[10px] text-sm font-semibold transition-colors duration-150 border cursor-pointer ${
              pullingLead || availableCount === 0
                ? 'border-slate-200 text-slate-300 bg-white cursor-not-allowed'
                : 'border-teal-800 text-teal-800 bg-white hover:bg-teal-50'
            }`}
          >
            {pullingLead ? (
              <Loader2 size={16} className="animate-spin" />
            ) : (
              <ArrowDownToLine size={16} />
            )}
            {t('pipeline.nextLead', 'Next Lead')}
            {availableCount !== null && (
              <span className={`text-xs font-medium px-1.5 py-0.5 rounded-full ${
                availableCount === 0
                  ? 'bg-slate-100 text-slate-400'
                  : 'bg-teal-50 text-teal-800'
              }`}>
                {availableCount}
              </span>
            )}
          </button>

          {/* Add new */}
          <button
            onClick={() => handleAddClick('lead')}
            className="flex items-center gap-2 px-4 py-2 rounded-[10px] text-sm font-semibold text-white bg-teal-800 hover:bg-teal-900 transition-colors duration-150 cursor-pointer"
          >
            <Plus size={16} />
            {t('pipeline.newLead', 'New Lead')}
          </button>
        </div>
      </div>

      {/* Stats bar */}
      {showStats && (
        <div className="flex items-center gap-3 mb-4 p-3 rounded-[12px] bg-white border border-slate-200 shadow-[0_1px_3px_rgba(0,0,0,0.04)]">
          {translatedStages.map(stage => (
            <div
              key={stage.id}
              className="flex items-center gap-2 px-3 py-1.5 rounded-lg"
              style={{ backgroundColor: stage.bg }}
            >
              <div
                className="w-2 h-2 rounded-full"
                style={{ backgroundColor: stage.color }}
              />
              <span className="text-xs font-medium text-slate-600">{stage.label}</span>
              <span className="text-sm font-bold" style={{ color: stage.headerBg }}>
                {stats.byStage[stage.id] || 0}
              </span>
            </div>
          ))}
        </div>
      )}

      {/* Kanban columns */}
      <div className="flex-1 overflow-x-auto overflow-y-hidden">
        <div className="flex gap-3 h-full pb-2 min-w-0">
          <DndContext
            sensors={sensors}
            collisionDetection={closestCorners}
            onDragStart={handleDragStart}
            onDragEnd={handleDragEnd}
          >
            {translatedStages.map(stage => (
              <PipelineColumn
                key={stage.id}
                stage={stage}
                leads={displayLeads.filter(l => l.stage === stage.id)}
                onLeadClick={handleLeadClick}
                onAddClick={handleAddClick}
              />
            ))}

            <DragOverlay>
              {activeLead ? (
                <div className="w-[260px] opacity-90 rotate-2">
                  <LeadCard lead={activeLead} onClick={() => {}} stage={stages.find(s => s.id === activeLead.stage)} />
                </div>
              ) : null}
            </DragOverlay>
          </DndContext>
        </div>
      </div>

      {/* Lead profile modal */}
      <LeadProfile
        lead={selectedLead}
        onClose={() => setSelectedLead(null)}
        onEdit={handleEditLead}
        onDelete={handleDeleteLead}
        onAddContact={handleOpenContact}
        onEnrol={handleEnrol}
        onUpdateLead={handleUpdateLeadField}
        stages={translatedStages}
      />

      {/* Dialogs */}
      <LeadDialog
        open={leadDialogOpen}
        onClose={() => { setLeadDialogOpen(false); setEditingLead(null); }}
        onSave={handleSaveLead}
        lead={editingLead}
        defaultStage={defaultStage}
        onCheckPhone={checkPhone}
        onViewLead={handleViewDuplicateLead}
      />

      <ContactLogDialog
        open={contactDialogOpen}
        onClose={() => { setContactDialogOpen(false); setContactLead(null); }}
        onSave={handleSaveContact}
        leadName={contactLead?.name || contactLead?.phone || ''}
      />

      <PipelineHelp
        open={helpOpen}
        onClose={() => setHelpOpen(false)}
      />

      <EnrolDialog
        lead={enrolLead}
        open={enrolDialogOpen}
        onClose={() => { setEnrolDialogOpen(false); setEnrolLead(null); }}
        onConverted={handleConverted}
        convertLead={convertLead}
      />
    </div>
    );
  }
